<template>
  <div class="team-table-container">
    <ve-table
      max-height="calc(100vh - 35rem)"
      :fixed-header="true"
      :columns="columns"
      :table-data="tableData"
    />
    <div v-if="isLoading" class="teams-loader">
      <spinner />
      <span>{{
        $t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.LOADING_MESSAGE')
      }}</span>
    </div>
    <empty-state
      v-else-if="!isLoading && !teamMetrics.length"
      :title="$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.NO_AGENTS')"
    />
    <div v-if="teamMetrics.length > 0" class="table-pagination">
      <ve-pagination
        :total="teamsList.length"
        :page-index="pageIndex"
        :page-size="50"
        :page-size-option="[50]"
        @on-page-number-change="onPageNumberChange"
      />
    </div>
  </div>
</template>

<script>
import { VeTable, VePagination } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  name: 'TeamTable',
  components: {
    EmptyState,
    Spinner,
    VeTable,
    VePagination,
  },
  mixins: [rtlMixin],
  props: {
    teamsList: {
      type: Array,
      default: () => [],
    },
    teamMetrics: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    pageIndex: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    tableData() {
      if (!Array.isArray(this.teamMetrics)) {
        return [];
      }

      const sortedMetrics = [...this.teamMetrics].sort(
        (a, b) => (b.metric.urgent || 0) - (a.metric.urgent || 0)
      );

      return sortedMetrics.map(team => ({
        team: team.name,
        thumbnail: team.avatar_url,
        open: team.metric.open || 0,
        unattended: team.metric.unattended || 0,
        none: team.metric.none || 0,
        low: team.metric.low || 0,
        medium: team.metric.medium || 0,
        high: team.metric.high || 0,
        urgent: team.metric.urgent || 0,
      }));
    },
    columns() {
      return [
        {
          field: 'team',
          key: 'team',
          title: this.$t(
            'OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.AGENT'
          ),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 25,
          renderBodyCell: ({ row }) => (
            <div class="row-user-block">
              <Thumbnail
                src={row.thumbnail}
                size="32px"
                username={row.team}
                status={row.status}
              />
              <div class="user-block">
                <h6 class="title text-truncate">{row.team}</h6>
                <span class="sub-title">{row.email}</span>
              </div>
            </div>
          ),
        },
        {
          field: 'open',
          key: 'open',
          title: this.$t(
            'OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.OPEN'
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'none',
          key: 'none',
          renderHeaderCell: () => (
            <span class="priority-undefined">
              {this.$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.NONE')}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'low',
          key: 'low',
          renderHeaderCell: () => (
            <span class="priority-low">
              {this.$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.LOW')}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'medium',
          key: 'medium',
          renderHeaderCell: () => (
            <span class="priority-medium">
              {this.$t(
                'OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.MEDIUM'
              )}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'high',
          key: 'high',
          renderHeaderCell: () => (
            <span class="priority-high">
              {this.$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.HIGH')}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'urgent',
          key: 'urgent',
          renderHeaderCell: () => (
            <span class="priority-urgent">
              {this.$t(
                'OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.URGENT'
              )}
            </span>
          ),
          align: 'center',
          width: 10,
        },
      ];
    },
  },
  methods: {
    getPriorityClass(priority) {
      switch (priority) {
        case 'urgent':
          return 'priority-urgent';
        case 'high':
          return 'priority-high';
        case 'medium':
          return 'priority-medium';
        case 'low':
          return 'priority-low';
        default:
          return 'priority-undefined';
      }
    },
    onPageNumberChange(pageIndex) {
      this.$emit('page-change', pageIndex);
    },
    getTeamInformation(id) {
      return this.teamsList.find(team => team.id === Number(id));
    },
  },
};
</script>

<style lang="scss" scoped>
.team-table-container {
  display: flex;
  flex-direction: column;
  flex: 1;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    background-color: transparent;
  }

  &::v-deep .ve-pagination-select {
    display: none;
  }

  .row-user-block {
    align-items: center;
    display: flex;
    text-align: left;

    .user-block {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      min-width: 0;
      margin: 0 var(--space-small);

      .title {
        font-size: var(--font-size-small);
        margin: var(--zero);
        line-height: 1.2;
      }
      .sub-title {
        font-size: var(--font-size-mini);
      }
    }
  }

  .table-pagination {
    margin-top: var(--space-normal);
    text-align: right;
  }
}

.teams-loader {
  align-items: center;
  display: flex;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-large);
}
.priority-urgent {
  background-color: var(--urgent-color);
}
.priority-high {
  background-color: var(--high-color);
}
.priority-medium {
  background-color: var(--medium-color);
}
.priority-low {
  background-color: var(--low-color);
}
.priority-undefined {
  background-color: var(--none-color);
}
.priority-urgent,
.priority-high,
.priority-medium,
.priority-low,
.priority-undefined {
  display: inline-block;
  color: var(--white);
  padding: var(--space-smaller) var(--space-slab);
  border-radius: var(--space-small);
  font-weight: var(--font-weight-bold);
  text-align: center;
}
.team-table-container::v-deep {
  .ve-table {
    thead.ve-table-header .ve-table-header-tr .ve-table-header-th {
      font-size: var(--font-size-mini);
      padding: var(--space-small) var(--space-two);
    }
    tbody.ve-table-body .ve-table-body-tr .ve-table-body-td {
      padding: var(--space-slab) var(--space-two);
    }
  }
}
</style>
